import { FormEvent, useState } from "react";
import { categories, testimonials } from "@/mocks/home";
import SectionHeading from "./SectionHeading";

const avatarColors = [
  "bg-primary-200 text-primary-800",
  "bg-accent-200 text-accent-800",
  "bg-secondary-200 text-secondary-800",
];

const SUBMIT_ADDR = "https://readdy.ai/api/form/dag4e6bep053ijub23ng";

const points = [
  { icon: "ri-compass-3-line", title: "定位", desc: "一个中立的热点观点聚合与讨论社区，不做情绪放大器。" },
  { icon: "ri-lightbulb-line", title: "理念", desc: "相信理性碰撞好过一边倒，让不同的声音都能被看到。" },
  { icon: "ri-check-double-line", title: "内容原则", desc: "重依据、讲分寸，转载注明来源，对未经证实的传闻不下定论。" },
  { icon: "ri-team-line", title: "我们", desc: "由一群爱冲浪、更爱讲道理的编辑与老用户共同维护秩序。" },
];

function StarRow() {
  return (
    <span className="flex items-center gap-0.5 text-secondary-500" aria-label="五星评价">
      {Array.from({ length: 5 }).map((_, i) => (
        <i key={i} className="ri-star-fill text-sm" aria-hidden="true"></i>
      ))}
    </span>
  );
}

export default function About() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypot && honeypot.value.trim() !== "") {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    const fd = new FormData(form);
    fd.delete("phone_alt");
    const body = new URLSearchParams(fd as unknown as URLSearchParams).toString();

    try {
      const res = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });
      const text = await res.text();
      let code = "";
      let serverMsg = "";
      try {
        const parsed = JSON.parse(text);
        code = parsed?.code ?? "";
        serverMsg = parsed?.meta?.message ?? parsed?.message ?? parsed?.meta?.detail ?? "";
      } catch {
        /* 非 JSON 时忽略 */
      }
      const ok = res.ok && code === "OK" && !/spam/i.test(serverMsg + " " + text);
      if (ok) {
        setStatus("success");
        setFormError("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，暂时无法提交，请稍后再试。");
    }
  };

  return (
    <section id="about" className="bg-background-100 py-12 md:py-16">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
        <SectionHeading
          kicker="关于我们 · 欢迎加入"
          title="关于宿野吃瓜"
          desc="从一句“今天这个瓜你怎么看”出发，我们想做一个讲道理、也讲温度的讨论社区。"
          align="center"
        />

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="flex flex-col gap-4">
            <div className="rounded-2xl border border-background-200 bg-background-50 p-6 md:p-7">
              <h3 className="text-xl font-bold text-foreground-950 md:text-2xl">把热点嚼明白，也把话说透</h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                宿野吃瓜诞生于一个朴素的观察：热点事件发生的那一刻，真正稀缺的不是情绪，而是能帮人理清头绪的讨论。
                我们收集各路瓜友的热评、观点与争议焦点，把同一件事的多方视角摆到台面上，供你快速了解舆论风向，
                再决定是参与讨论，还是安心围观。
              </p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                这里不追求把瓜越吃越大，只希望每一次讨论都离“把事说清楚”更近一点。欢迎你来，带着自己的判断。
              </p>
              <a
                href="#guide"
                className="mt-4 inline-flex items-center whitespace-nowrap text-sm font-medium text-primary-600 hover:text-primary-700"
              >
                不了解规矩？先看讨论指南
                <i className="ri-arrow-right-line ml-1" aria-hidden="true"></i>
              </a>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {points.map((p) => (
                <div key={p.title} className="rounded-2xl border border-background-200 bg-background-50 p-5">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                    <i className={p.icon} aria-hidden="true"></i>
                  </span>
                  <h4 className="mt-3 text-base font-bold text-foreground-950">{p.title}</h4>
                  <p className="mt-1 text-sm leading-relaxed text-foreground-700">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-background-200 bg-background-50 p-6 md:p-7">
            <h3 className="text-lg font-bold text-foreground-950 md:text-xl">意见反馈 / 投稿</h3>
            <p className="mt-1.5 text-sm text-foreground-600">说说你想吐槽的、想投稿的，或建议补充的板块。</p>

            {status === "success" && (
              <div className="mt-4 flex items-center gap-2 rounded-lg bg-accent-100 px-4 py-3 text-sm text-accent-800" role="status">
                <i className="ri-checkbox-circle-fill" aria-hidden="true"></i>
                已收到，感谢你的反馈！
              </div>
            )}
            {status === "error" && (
              <div className="mt-4 flex items-start gap-2 rounded-lg bg-primary-100 px-4 py-3 text-sm text-primary-800" role="alert">
                <i className="ri-error-warning-fill mt-0.5" aria-hidden="true"></i>
                {formError}
              </div>
            )}

            <form
              onSubmit={onSubmit}
              data-readdy-form="dag4e6bep053ijub23ng"
              className="mt-5 space-y-4"
              aria-label="意见反馈与投稿表单"
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="fb-name" className="mb-1.5 block text-sm text-foreground-700">
                    怎么称呼你
                  </label>
                  <input
                    id="fb-name"
                    name="name"
                    type="text"
                    required
                    maxLength={30}
                    placeholder="你的昵称"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                </div>
                <div>
                  <label htmlFor="fb-email" className="mb-1.5 block text-sm text-foreground-700">
                    联系邮箱
                  </label>
                  <input
                    id="fb-email"
                    name="email"
                    type="email"
                    required
                    placeholder="name@example.com"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="fb-topic" className="mb-1.5 block text-sm text-foreground-700">
                  想聊的板块
                </label>
                <select
                  id="fb-topic"
                  name="topic"
                  defaultValue=""
                  required
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                >
                  <option value="" disabled>
                    请选择一个板块
                  </option>
                  {categories.map((c) => (
                    <option key={c.name} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                  <option value="其他">其他 / 综合建议</option>
                </select>
              </div>

              <div>
                <label htmlFor="fb-message" className="mb-1.5 block text-sm text-foreground-700">
                  反馈或投稿内容
                </label>
                <textarea
                  id="fb-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="尽量描述清楚：是举报问题、投稿话题，还是改进建议？（不超过 500 字）"
                  className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                ></textarea>
              </div>

              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="hp-field"
              />

              <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-foreground-600">
                  可直接发邮件至{" "}
                  <a
                    href="mailto:feedback@suye-chigua.example"
                    className="text-primary-600 underline-offset-2 hover:underline"
                  >
                    feedback@suye-chigua.example
                  </a>
                </p>
                <button
                  type="submit"
                  className="inline-flex w-full items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 sm:w-auto"
                >
                  <i className="ri-send-plane-fill mr-1.5" aria-hidden="true"></i>
                  提交反馈
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="mt-10">
          <h3 className="text-center text-xl font-bold text-foreground-950 md:text-2xl">用户怎么说</h3>
          <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-3">
            {testimonials.map((t, i) => (
              <figure
                key={t.name}
                className="flex flex-col rounded-2xl border border-background-200 bg-background-50 p-6"
              >
                <StarRow />
                <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-foreground-800">
                  “{t.text}”
                </blockquote>
                <figcaption className="mt-5 flex items-center gap-3 border-t border-background-200 pt-4">
                  <span
                    className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold ${
                      avatarColors[i % avatarColors.length]
                    }`}
                    aria-hidden="true"
                  >
                    {t.avatar}
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-foreground-950">{t.name}</span>
                    <span className="text-xs text-foreground-600">{t.tag}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}